import {
  BaseWorkspacesRepository,
  WorkspaceResource,
  WorkspacesList,
  UpdateWorkspaceData,
} from "./BaseWorkspacesRepository.interface";
import { localApiBase } from "../../../lib/localApi";

class ApiWorkspacesRepository implements BaseWorkspacesRepository {
  private get Domain(): string {
    return localApiBase();
  }

  async getAll(): Promise<WorkspacesList | null> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces`);
      const result = await response.json();

      if (result?.data && Array.isArray(result.data)) {
        return result.data as WorkspacesList;
      }

      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async create(workspace: Omit<WorkspaceResource, "current">): Promise<WorkspaceResource | null> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(workspace),
      });

      const result = await response.json();

      if (result?.data) {
        return result.data as WorkspaceResource;
      }

      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getCurrent(): Promise<string> {
    const response = await fetch(`${this.Domain}/api/workspace`);
    const result = await response.json();
    return result?.data || "";
  }

  async use(path: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.Domain}/api/workspace/use`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path }),
      });
      return response.ok;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async delete(workspaceId: string): Promise<{ error: boolean; message: string }> {
    try {
      const response = await fetch(`${this.Domain}/api/workspaces/${workspaceId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return {
          error: false,
          message: result.message || "Workspace deleted successfully",
        };
      }

      return {
        error: true,
        message: result.message || "Error deleting workspace",
      };
    } catch (e) {
      console.error("Error deleting workspace:", e);
      return {
        error: true,
        message: "Error connecting to delete workspace",
      };
    }
  }

  async update(
    workspaceId: string,
    data: UpdateWorkspaceData
  ): Promise<{ error: boolean; message: string; data?: WorkspaceResource }> {
    try {
      const hasBlobs = data.logo instanceof Blob || data.cover instanceof Blob;
      let response: Response;

      if (hasBlobs) {
        const formData = new FormData();
        if (data.name) formData.append("name", data.name);
        if (data.description) formData.append("description", data.description);
        if (data.isPublic !== undefined) formData.append("isPublic", String(data.isPublic));

        if (data.logo instanceof Blob) {
          formData.append("logo", data.logo, "logo.png");
        }

        if (data.cover instanceof Blob) {
          formData.append("cover", data.cover, "cover.png");
        }

        response = await fetch(`${this.Domain}/api/workspaces/${workspaceId}`, {
          method: "PUT",
          body: formData,
        });
      } else {
        response = await fetch(`${this.Domain}/api/workspaces/${workspaceId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      }

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return {
            error: false,
            message: result.message || "Workspace updated successfully",
            data: result.data,
          };
        }
      }

      const result = await response.json();
      return {
        error: true,
        message: result.message || "Error updating workspace",
      };
    } catch (e) {
      console.error("Error updating workspace:", e);
      return {
        error: true,
        message: "Error connecting to update workspace",
      };
    }
  }
}

export default ApiWorkspacesRepository;
